import React from 'react';
import { createPortal } from 'react-dom';
import { fmt } from './oilApi';
import { OilIcon } from './OilIcons';
import { pct } from './oilMafiaText';
import { sfxClose } from './oilSounds';

const Sec = ({ n, title, children, testid }) => (
  <div className="mt-2.5 rounded-xl border border-zinc-800 bg-black/50 p-3" data-testid={testid}>
    <p className="mb-1.5 flex items-center gap-1.5 text-[10px] font-black uppercase tracking-[0.15em] text-amber-400">
      <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[9px] font-black text-black">{n}</span>
      {title}
    </p>
    <div className="space-y-1 text-[10px] leading-relaxed text-zinc-300">{children}</div>
  </div>
);

const Li = ({ children }) => (
  <p className="flex gap-1.5">
    <span className="shrink-0 text-amber-600">▸</span><span>{children}</span>
  </p>
);

export const OilMafiaRules = ({ config = {}, onClose }) => {
  const limits = config.limits || {};
  const services = config.services || [];
  const tiers = (config.shield?.tiers || []).slice().sort(
    (a, b) => (b.block_pct || 0) - (a.block_pct || 0));
  const prizes = (config.wanted?.prizes || []).filter((p) => Number(p.coins) > 0);

  return createPortal(
    <div className="fixed inset-0 z-[97] flex items-center justify-center bg-black/90 backdrop-blur-sm"
      data-testid="oil-rules-overlay" onClick={onClose}>
      <div className="w-full max-w-md p-3" onClick={(e) => e.stopPropagation()}>
        <div className="p-[2px]" style={{ background: 'linear-gradient(160deg, #f2d387, #6e5118 40%, #c99b3f 70%, #f2d387)', borderRadius: '18px' }}>
          <div className="overflow-hidden rounded-[16px] bg-[#0b0906] p-3.5">
            <div className="mb-1 flex items-center justify-between">
              <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-amber-300">
                <img src="/oil/ic_regras.webp" alt="" className="h-7 w-7 object-contain" />
                Regras da Máfia
              </p>
              <button onClick={() => { sfxClose(); onClose(); }} data-testid="oil-rules-close"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-black/70 text-sm font-black text-amber-300">✕</button>
            </div>

            <div className="max-h-[72vh] overflow-y-auto pr-0.5" data-testid="oil-rules-body">
              <p className="rounded-xl border border-amber-900/50 bg-amber-950/20 p-3 text-[10px] italic leading-relaxed text-amber-100">
                Bem-vindo à <b className="text-amber-300">Terra dos Mafiosos</b>: a parte suja da Bolsa do
                Petróleo. Aqui os magnatas contratam serviços por baixo do pano para queimar o FOX Dólar
                dos rivais, comprar proteção e construir Respeito nas ruas. Todo o dinheiro desta aba é do
                jogo — nada aqui mexe no seu saldo real de moedas.
              </p>

              <Sec n="1" title="As duas moedas da rua" testid="oil-rules-currency">
                <Li><b className="text-amber-300"><OilIcon k="black" /> Black Coins</b> — é o dinheiro que
                  paga capangas, escudos e serviços sujos. Você ganha nas recargas e nas missões diárias.</Li>
                <Li><b className="text-rose-300"><OilIcon k="respect" /> Respeito</b> — sua reputação. Não
                  compra nada e não vale dinheiro: serve para te colocar na Lista dos Mais Procurados.</Li>
                <Li><b className="text-emerald-300"><OilIcon k="fox" /> FOX Dólar</b> — o patrimônio do seu
                  império, alvo dos ataques. Quando você toma um golpe ele é <b>queimado</b>, nunca
                  transferido para o atacante.</Li>
              </Sec>

              <Sec n="2" title="Serviços sujos — tabela oficial" testid="oil-rules-services">
                <div className="-mx-0.5 overflow-hidden rounded-lg border border-zinc-800">
                  <div className="flex bg-zinc-900/80 px-2 py-1 text-[8.5px] font-black uppercase tracking-wider text-amber-500">
                    <span className="flex-1">Serviço</span>
                    <span className="w-12 text-right">Custo</span>
                    <span className="w-10 text-right">Êxito</span>
                    <span className="w-11 text-right">Dano</span>
                  </div>
                  {services.map((s) => (
                    <div key={s.id} data-testid={`oil-rules-svc-${s.id}`}
                      className="flex items-center border-t border-zinc-800/70 px-2 py-1 text-[9.5px]">
                      <span className="flex-1 truncate pr-1 font-bold text-zinc-100">{s.name}</span>
                      <span className="w-12 text-right font-black text-amber-300">{fmt(s.cost_black_coins)}</span>
                      <span className="w-10 text-right text-zinc-300">{pct(s.success_probability)}</span>
                      <span className="w-11 text-right font-bold text-red-400">
                        {s.offensive ? `${s.damage_pct}%` : '—'}
                      </span>
                    </div>
                  ))}
                </div>
                <Li>Dano é sempre uma % do FOX Dólar <b>do alvo</b>, limitada
                  a <b className="text-amber-300">{fmt(limits.max_damage_per_attack_fox)}</b> por golpe.</Li>
                <Li><b>Espionagem Industrial</b> não é ataque: não faz dano, não avisa o alvo e não gasta
                  operação do dia.</Li>
                <Li><b className="text-orange-300">DANO NAS INSTALAÇÕES:</b> a
                  {' '}<b>Sabotagem na Refinaria</b> amassa a <b>Usina de Perfuração</b> do alvo e o
                  {' '}<b>Vazamento no Oleoduto</b> amassa a <b>Envasadora</b> — sempre no nível mais alto
                  que ele tem, <b className="text-orange-300">35 de integridade</b> por acerto (de 100).
                  Três acertos quebram.</Li>
                <Li>Instalação em <b className="text-red-400">0 para de produzir</b> naquele nível até o dono
                  {' '}<b>pagar o reparo</b>. Reparo integral custa
                  {' '}<b className="text-amber-300">{limits.facility_repair_pct_of_price || 20}% do preço
                  do nível</b> e é proporcional ao dano — paga e volta a operar na hora. Acima de 0 a
                  instalação funciona normal, e escudo que bloqueia protege dela.</Li>
                <Li>Teto de <b className="text-orange-300">{limits.max_daily_facility_damage_per_target || 70} pontos
                  de dano em instalação por alvo por dia</b>, somando todos os atacantes.</Li>
              </Sec>

              <Sec n="3" title="Quantos serviços por dia" testid="oil-rules-limits">
                <Li><b>Quem pode ser alvo:</b> qualquer império do
                  {' '}<b className="text-amber-300">Ranking dos Magnatas</b> que ainda tenha império
                  de sobra — busque por apelido ou KAKO ID, ou desça a lista com
                  {' '}<b>Carregar mais alvos</b>.</Li>
                <Li><b className="text-rose-300">Teto de Respeito:</b> cada jogador ganha no máximo
                  {' '}<b className="text-amber-300">600 de Respeito por dia</b>. Depois disso os
                  serviços continuam funcionando (dano, roubo de carga e espionagem), mas
                  {' '}<b>não rendem mais Respeito</b> até a virada do dia.</Li>
                <Li>Você pode contratar até
                  {' '}<b className="text-amber-300">{limits.max_offensive_attacks_per_day} operações ofensivas por dia</b>
                  {' '}(o contador zera à meia-noite). Espionagens são ilimitadas.</Li>
                <Li>No <b>mesmo alvo</b> você pode bater até
                  {' '}<b className="text-amber-300">{limits.max_offensive_attacks_per_target_per_day || 3}x por dia</b>,
                  {' '}sempre respeitando a imunidade dele entre um golpe e outro — e o Respeito cai a
                  cada repetida (2º golpe metade, 3º zero).</Li>
                <Li><b className="text-rose-300">Revanche:</b> quem te acertou pode ser cobrado na hora pela
                  aba <b>Rixas</b> — a revanche vale mesmo que você já tenha estourado o limite naquele
                  alvo, <b>uma única vez por golpe sofrido</b>, usando o mesmo serviço que te atingiu.</Li>
                <Li>Quem acabou de tomar um golpe fica
                  {' '}<b className="text-amber-300">{limits.target_immunity_after_hit_hours}h imune</b> a
                  novos ataques (de qualquer pessoa).</Li>
                <Li>Novato tem
                  {' '}<b className="text-amber-300">{limits.new_player_pvp_protection_hours}h de proteção</b>
                  {' '}ao entrar na Season, e <b className="text-amber-300">impérios pequenos demais</b> não
                  podem ser atacados. O tamanho conta o FOX Dólar <b>e os barris prontos no galpão</b> —
                  segurar carga sem vender não protege ninguém.</Li>
                <Li>Ninguém pode perder mais
                  de <b className="text-amber-300">{limits.max_daily_damage_pct_of_fox}% do próprio FOX Dólar por dia</b>
                  {' '}somando todos os ataques recebidos.</Li>
                <Li>Nas últimas <b className="text-blue-300">{limits.final_truce_hours}h da Season</b> entra a
                  <b className="text-blue-300"> Trégua do Petróleo</b>: nenhum serviço ofensivo é aceito.</Li>
                <Li>Durante o evento <b>Fiscalização nas Docas</b> os serviços ficam suspensos até a poeira baixar.</Li>
              </Sec>

              <Sec n="4" title="Escudos — sua segurança" testid="oil-rules-shields">
                {tiers.map((t) => (
                  <Li key={t.id}>
                    <b className={t.id === 'ouro' ? 'text-amber-400' : t.id === 'prata' ? 'text-slate-300' : 'text-orange-400'}>
                      {t.name}
                    </b> — bloqueia <b>{Math.round((t.block_pct || 0) * 100)}%</b> dos golpes ·
                    custa <b className="text-amber-300"><OilIcon k="black" /> {fmt(t.cost_black_coins)}</b>
                  </Li>
                ))}
                <Li>Você pode manter os <b>3 escudos ativos</b> ao mesmo tempo (um de cada tipo).</Li>
                <Li>Ao ser atacado, o <b>escudo mais forte entra na frente</b> — e é <b>destruído no
                  confronto</b>, tenha bloqueado o golpe ou não. Reponha sempre.</Li>
              </Sec>

              <Sec n="5" title="Respeito e anti-farm" testid="oil-rules-respect">
                <Li>Acerto em cheio: <b className="text-rose-300">+100</b> ·
                  Escudo do alvo bloqueou: <b className="text-rose-300">+60</b> ·
                  Espionagem certeira: <b className="text-rose-300">+30</b> ·
                  Fracasso: <b>0</b>. (O Assalto ao Cofre paga mais: +120 e +70.)</Li>
                <Li>Teto de <b className="text-amber-300">600 de Respeito por dia</b>.</Li>
                <Li>Bater sempre no mesmo alvo rende cada vez menos: o 1º golpe do dia nele paga
                  {' '}<b>cheio</b>, o 2º paga <b>metade</b> e do 3º em diante <b>nada</b>.</Li>
              </Sec>

              <Sec n="6" title="Lista dos Mais Procurados" testid="oil-rules-wanted">
                <Li>Ranking dos <b>10 magnatas com mais Respeito</b> na Season; o Top 3 ganha cartaz de
                  <b> PROCURA-SE • VIVO OU MORTO</b> na parede.</Li>
                <Li>Títulos: 1º <b className="text-amber-300">Inimigo Público Nº 1</b> ·
                  2º Procurado pela FOX Bureau · 3º Foragido das Docas.</Li>
                {prizes.length > 0 ? (
                  <>
                    <Li><b className="text-emerald-300">PRÊMIO EM MOEDAS:</b> quem terminar a Season no
                      Top 3 dos Procurados recebe moedas de verdade na conta KAKO:</Li>
                    {prizes.map((p) => (
                      <p key={p.rank} className="ml-3 text-[10px] font-bold text-emerald-200"
                        data-testid={`oil-rules-prize-${p.rank}`}>
                        {p.rank}º lugar — {fmt(p.coins)} moedas
                      </p>
                    ))}
                    <Li>O crédito é feito <b>só depois que o administrador libera</b> na finalização
                      da Season — o servidor salva os ganhadores e paga uma única vez, direto no
                      KAKO ID de cada um.</Li>
                  </>
                ) : (
                  <Li>Ser procurado é status: <b>não dá prêmio em dinheiro</b> e não protege ninguém de
                    tomar golpe.</Li>
                )}
              </Sec>

              <Sec n="7" title="Informantes e Rixas" testid="oil-rules-intel">
                <Li><b className="text-amber-300">Informantes</b> é a sua caixa de recados: chega aviso toda
                  vez que alguém <b>te acerta</b> ou tenta e o seu escudo segura — com nome do sujeito e o
                  serviço usado.</Li>
                <Li><b className="text-amber-300">Rixas</b> é o histórico completo: tudo o que você
                  contratou (inclusive falhas e espionagens) e tudo o que chegou em você — com resultado,
                  dano, Respeito e custo. É lá que fica o botão de <b className="text-rose-300">Revanche</b>.</Li>
                <Li>Tentativa que <b>fracassou</b> não aparece para o alvo: ele nunca descobre que você tentou.</Li>
              </Sec>

              <Sec n="8" title="Jogo limpo" testid="oil-rules-fairplay">
                <Li>Todo o resultado é decidido no servidor: sorteio de sucesso, dano, escudo e Respeito.</Li>
                <Li>O FOX Dólar perdido em ataques é <b>destruído</b>, nunca vai para o atacante.</Li>
                <Li>Nada nesta aba movimenta moedas reais, saldo de compras ou comissões: é economia
                  100% do jogo.</Li>
              </Sec>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OilMafiaRules;
